<template>
  <div class="modal-overlay web-auth-modal" v-if="visible" @click.self="close">
    <div class="modal-box">
      <div class="auth-box">
        <div class="logo">
          <div class="logo-icon"><img :src="logoImg" alt="logo" class="logo-icon-img" /></div>
          <h1>忘记密码</h1>
          <p class="sub">通过邮箱验证码重置登录密码</p>
        </div>
        <div class="step-bar">
          <div class="step-dot" :class="{ active: step === 0, done: step > 0 }"></div>
          <div class="step-dot" :class="{ active: step === 1, done: step > 1 }"></div>
        </div>

        <!-- Step 0: Email + Code -->
        <div v-if="step === 0">
          <div class="step-title">邮箱验证</div>
          <div class="step-desc">请输入注册邮箱并完成验证</div>
          <div class="form-group">
            <label>邮箱</label>
            <div class="input-wrap">
              <input type="email" v-model="form.email" placeholder="请输入注册邮箱" @input="clearError">
            </div>
          </div>
          <div v-if="errors.email" class="field-error">{{ errors.email }}</div>
          <div class="form-group">
            <label>验证码</label>
            <div class="input-wrap code-row">
              <input type="text" v-model="form.code" placeholder="6位邮箱验证码" maxlength="6" @input="clearError">
              <button type="button" class="btn-code" @click="sendCode" :disabled="codeCooldown > 0">{{ codeCooldown > 0 ? codeCooldown + 's' : '获取验证码' }}</button>
            </div>
          </div>
          <div v-if="errors.code" class="field-error">{{ errors.code }}</div>
          <div v-if="errors.general" class="form-error">{{ errors.general }}</div>
          <button class="btn-primary" @click="nextStep0" :disabled="loading">{{ loading ? '验证中...' : '下一步' }}</button>
        </div>

        <!-- Step 1: New Password -->
        <div v-else-if="step === 1">
          <div class="step-title">设置新密码</div>
          <div class="step-desc">密码 6-20 位，建议包含字母和数字</div>
          <div class="form-group">
            <label>新密码</label>
            <div class="input-wrap">
              <input type="password" v-model="form.newPassword" placeholder="请输入新密码" maxlength="20" @input="clearError">
            </div>
          </div>
          <div v-if="errors.newPassword" class="field-error">{{ errors.newPassword }}</div>
          <div class="form-group">
            <label>确认新密码</label>
            <div class="input-wrap">
              <input type="password" v-model="form.confirmPassword" placeholder="再次输入新密码" @input="clearError">
            </div>
          </div>
          <div v-if="errors.confirmPassword" class="field-error">{{ errors.confirmPassword }}</div>
          <div v-if="errors.general" class="form-error">{{ errors.general }}</div>
          <button class="btn-primary" @click="doReset" :disabled="loading">{{ loading ? '提交中...' : '重置密码' }}</button>
        </div>

        <!-- Step 2: Success -->
        <div v-else-if="step === 2">
          <div class="success-icon">✓</div>
          <div class="step-title" style="text-align:center">密码重置成功</div>
          <div class="step-desc" style="text-align:center">请使用新密码登录</div>
          <button class="btn-primary" @click="goToLogin">返回登录</button>
        </div>

        <div class="auth-link" v-if="step < 2">
          想起密码了？<a href="javascript:void(0)" @click="$emit('switch-login')">返回登录</a>
        </div>
      </div>
      <div style="text-align:center;margin-top:12px">
        <a href="javascript:void(0)" @click="close" style="color:#999;font-size:13px;text-decoration:none">关闭</a>
      </div>
    </div>
  </div>
</template>

<script>
import { showToast } from '@/utils/common'
import { isValidEmail } from '@/utils/validate'
import { resetPassword } from '@/api/auth'

export default {
  name: 'ForgotPasswordModal',
  props: { visible: Boolean },
  data() {
    return {
      logoImg: require('@/assets/logo/logo-icon.png'),
      step: 0,
      form: { email: '', code: '', newPassword: '', confirmPassword: '' },
      errors: { email: '', code: '', newPassword: '', confirmPassword: '', general: '' },
      codeCooldown: 0,
      loading: false
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    clearError() {
      this.errors = { email: '', code: '', newPassword: '', confirmPassword: '', general: '' }
    },
    async sendCode() {
      if (!this.form.email) { this.errors.email = '请输入邮箱'; return }
      if (!isValidEmail(this.form.email)) { this.errors.email = '邮箱格式不正确'; return }
      this.clearError()
      this.codeCooldown = 60
      const timer = setInterval(() => { this.codeCooldown--; if (this.codeCooldown <= 0) clearInterval(timer) }, 1000)
      try {
        await this.$store.dispatch('user/sendCode', { email: this.form.email, scene: 'reset_password' })
        showToast('验证码已发送，请查收邮箱')
      } catch (e) {
        clearInterval(timer)
        this.codeCooldown = 0
        this.errors.general = e.message || '发送验证码失败'
      }
    },
    async nextStep0() {
      this.clearError()
      if (!this.form.email) { this.errors.email = '请输入邮箱'; return }
      if (!isValidEmail(this.form.email)) { this.errors.email = '邮箱格式不正确'; return }
      if (!this.form.code || this.form.code.length !== 6) { this.errors.code = '请输入6位验证码'; return }
      // 前端校验通过，进入下一步（验证码在最终提交时由后端校验）
      this.step = 1
    },
    async doReset() {
      this.clearError()
      if (!this.form.newPassword || this.form.newPassword.length < 6) { this.errors.newPassword = '密码不能少于6位'; return }
      if (this.form.newPassword.length > 20) { this.errors.newPassword = '密码不能超过20位'; return }
      if (this.form.newPassword !== this.form.confirmPassword) { this.errors.confirmPassword = '两次密码不一致'; return }
      this.loading = true
      try {
        const res = await resetPassword({
          email: this.form.email,
          code: this.form.code,
          newPassword: this.form.newPassword,
          confirmPassword: this.form.confirmPassword
        })
        if (res.code === 200) {
          this.step = 2
        } else {
          this.errors.general = res.msg || '重置密码失败'
        }
      } catch (e) {
        const msg = e.message || '重置密码失败'
        this.errors.general = msg
        // 验证码相关错误，回到第一步
        if (msg.indexOf('验证码') !== -1 || msg.indexOf('过期') !== -1) {
          this.step = 0
          this.form.code = ''
        }
      }
      this.loading = false
    },
    goToLogin() {
      this.$emit('switch-login')
      this.close()
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.step = 0
        this.form = { email: '', code: '', newPassword: '', confirmPassword: '' }
        this.errors = { email: '', code: '', newPassword: '', confirmPassword: '', general: '' }
        this.codeCooldown = 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.web-auth-modal .modal-box { width: 420px; padding: 32px 40px; }
.auth-box { width: 100%; }
.auth-box .logo { text-align: center; margin-bottom: 32px; }
.auth-box .logo-icon {
  width: 56px; height: 56px; background: transparent; border-radius: 14px;
  margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;
}
.auth-box .logo-icon-img {
  width: 56px; height: 56px;
}
.auth-box h1 { font-size: 22px; text-align: center; }
.auth-box .sub { text-align: center; color: var(--text-secondary); font-size: 13px; margin-top: 4px; }
.step-bar { display: flex; justify-content: center; gap: 8px; margin-bottom: 24px; }
.step-dot { width: 8px; height: 8px; border-radius: 50%; background: #ddd; transition: all .3s; }
.step-dot.active { width: 24px; border-radius: 4px; background: var(--red); }
.step-dot.done { background: var(--green); }
.step-title { font-size: 16px; font-weight: 600; margin-bottom: 4px; }
.step-desc { font-size: 13px; color: var(--text-secondary); margin-bottom: 20px; }
.code-row { display: flex; gap: 8px; input { flex: 1; } }
.btn-code {
  white-space: nowrap; padding: 0 12px; background: transparent; color: var(--red);
  border: none; font-size: 13px; cursor: pointer; font-family: inherit;
  &:disabled { color: var(--text-muted); cursor: not-allowed; }
}
.field-error { font-size: 12px; color: var(--red); margin-top: 4px; margin-bottom: 8px; }
.form-error { text-align: center; color: var(--red); font-size: 13px; margin-top: 12px; }
.success-icon {
  width: 72px; height: 72px; border-radius: 50%; background: var(--green);
  margin: 0 auto 16px; display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 36px; font-weight: 700;
}
.btn-primary {
  width: 100%; padding: 12px; background: var(--red); color: #fff;
  border: none; border-radius: 6px; font-size: 15px; font-weight: 600;
  cursor: pointer; font-family: inherit;
  &:hover { opacity: .92; } &:disabled { opacity: .5; cursor: not-allowed; }
}
.auth-link { text-align: center; margin-top: 16px; font-size: 14px; color: var(--text-secondary); a { color: var(--red); text-decoration: none; font-weight: 600; } }
</style>
